import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { api, type SubjectType } from '../lib/api';
import { Card, CardContent } from '../components/ui';

interface InboxThread {
  id: string;
  title: string | null;
  unread: number;
  muted: boolean;
  last_message_at: string;
  subject: { type: SubjectType; label: string; city: string | null; href: string } | null;
  // B7: the design doc asks the inbox to show "the last message", and it showed only a
  // timestamp. Already truncated server-side (PREVIEW_MAX), so this renders as-is.
  last_message: {
    body: string;
    kind: 'human' | 'ai_answer' | 'ai_volunteer';
    author_id: string | null;
    created_at: string;
  } | null;
}

export default function ThreadsInbox() {
  const [threads, setThreads] = useState<InboxThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .getThreadInbox()
      .then((r) => setThreads(r.threads))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load threads'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-4">
        <div>
          <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Property Threads</h1>
          <p className="text-gray-500 mt-1">Every property you and Robert are talking about</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : threads.length === 0 && !error ? (
          // Gated on !error - an empty list here can mean two different things: "there are
          // genuinely no threads" or "the fetch failed and we have no idea." Rendering this
          // copy on a failed fetch would claim the second case is the first, right next to the
          // error banner above saying otherwise. This app has already had one real Supabase
          // outage, so that is not a hypothetical.
          <Card>
            <CardContent className="py-8 text-center text-sm text-gray-500">
              No conversations yet. Open a property and start one from the Conversation panel.
            </CardContent>
          </Card>
        ) : threads.length > 0 ? (
          <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg bg-white">
            {threads.map((t) => (
              <Link
                key={t.id}
                to={t.subject?.href || '/threads'}
                className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50"
              >
                <div className="min-w-0">
                  <p className="text-sm text-gray-900 truncate">
                    {t.subject?.label || t.title || 'Untitled'}
                  </p>
                  {/* The preview is the line that makes the inbox usable once there is more
                      than a handful of threads - a timestamp does not help you choose which
                      to open. `truncate` matters: the server caps length, not line count. */}
                  {t.last_message ? (
                    <p className="text-xs text-gray-600 truncate">
                      {t.last_message.kind !== 'human' && (
                        <span className="text-warning font-medium">Assistant · </span>
                      )}
                      {t.last_message.body}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 italic truncate">No messages yet</p>
                  )}
                  <p className="text-xs text-gray-500 truncate">
                    {t.subject?.city ? `${t.subject.city} · ` : ''}
                    {new Date(t.last_message_at).toLocaleString()}
                  </p>
                </div>
                {t.unread > 0 && !t.muted && (
                  <span className="shrink-0 bg-primary-500 text-white text-xs rounded-full px-2 py-0.5">
                    {t.unread}
                  </span>
                )}
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    </Layout>
  );
}
